/* eslint-disable react/jsx-props-no-spreading */
import { useQuery } from '@tanstack/react-query';
import debounce from 'lodash-es/debounce';
import React, { useState } from 'react';

import { searchLocationType } from '../../../api/ventura/location';
import { useConfig } from '../../../hooks/useConfig';
import AutocompleteArray, { AutocompleteArrayProps } from '../../forms/AutocompleteArray';
import AutocompleteTextField from '../../forms/AutocompleteTextField';

export interface AutocompleteLocationFieldProps<
  T,
  Multiple extends boolean | undefined,
  DisableClearable extends boolean | undefined,
  FreeSolo extends boolean | undefined
> extends AutocompleteArrayProps<T, Multiple, DisableClearable, FreeSolo> {
  locationType: string;
  useLabelAsValue?: boolean;
}

function AutocompleteLocationField<
  T,
  Multiple extends boolean | undefined = undefined,
  DisableClearable extends boolean | undefined = undefined,
  FreeSolo extends boolean | undefined = undefined
>(props: Omit<AutocompleteLocationFieldProps<T, Multiple, DisableClearable, FreeSolo>, 'options'>) {
  const [open, setOpen] = useState(false);
  const { apiHost } = useConfig();
  const { locationType, multiple, useLabelAsValue = false, ...rest } = props;

  const [keyword, setKeyword] = useState('');

  const { data, isLoading: loading } = useQuery({
    queryKey: ['searchLocationType', locationType, keyword],
    queryFn: () => searchLocationType(apiHost, locationType, keyword),
    // Only fetch if user clicks on the autocomplete dropdown or is typing.
    enabled: open,
  });

  const throttledSetKeyword = debounce(setKeyword, 1000);

  const onInputChange = (event, value, reason) => {
    // Initiate a search on all reasons except when reason is 'reset'.
    // react-hook-forms will trigger that event when user chooses a value in the dropdown
    // and cause an extraneous search on the server with that chosen value.
    if (reason !== 'reset') {
      throttledSetKeyword(value);
    }
  };

  // If the item has a full_name, eg for cities where the full name will have the city and state, then use that as the label.
  // For the location field that accept only one value, it uses the label as the value.
  const options =
    data?.map((item) => ({ label: item.full_name || item.label, value: useLabelAsValue ? item.label : item.value })) ||
    [];

  let jsx;
  const jsxProps = {
    loading,
    onInputChange,
    options,
    ...rest,
    open,
    onOpen: () => {
      setOpen(true);
    },
    onClose: () => {
      setOpen(false);
    },
  };

  if (multiple) {
    jsx = <AutocompleteArray {...jsxProps} />;
  } else {
    jsx = <AutocompleteTextField {...jsxProps} />;
  }

  return jsx;
}

export default AutocompleteLocationField;
